import { API_ENDPOINT } from "@/constants/appConstants";
import { getTimeAppliedAgo } from "@/helpers/getTimeAppliedAgo";
import { APPLIED_JOBS_QUERY_KEY } from "@/lib/queryKeys";
import { getJDUrlWithDomain } from "@/utils/jobs";
import { MessageCard } from "@monsterindia/global-ui-components";
import { JobCard } from "@monsterindia/seeker-jobs";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useRef, useState } from "react";

import AutoApplyV2 from "@/components/common/autoApplyV2/AutoApplyV2";

import { useAppliedPageData } from "..";
import { useDashboardContext } from "../..";
import { generateJDUrl } from "../../utils/generateJDUrl";
import { getAdjustedHeight } from "../../utils/getDashboardAdjustedHeight";
import { useAppliedJobs } from "../query";
import {
  AppliedJobAPIKeysType,
  AppliedPageContextProps,
  AppliedPageDataType,
  DashboardContextProps,
  FilterStatusType,
} from "../types";
import "./components/styles.css";
import Skeleton from "./skeleton";

const AppliedJobCard = () => {
  const { isMobile, host }: DashboardContextProps = useDashboardContext();
  const {
    appliedJobData,
    setAppliedJobData,
    handleEventTracking,
    cleverTapPropertyValues,
  }: AppliedPageContextProps = useAppliedPageData();
  const [appliedJobParams, setAppliedJobParams] = useState({
    initialPage: 2,
    currentPage: 1,
    filterStatus: appliedJobData.selectedFilter?.[0]?.id || "",
    isFetchingEnabled: false,
    resetType: { value: "none" },
    loadingJobCard: true,
    lastDataLength: appliedJobData.data?.length,
  });
  const [cardClickedUrl, setCardClickedUrl] = useState<string | undefined>(
    undefined,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const queryClient = useQueryClient();

  const { data, isFetchingNextPage, isFetching, refetch, fetchNextPage } =
    useAppliedJobs(
      appliedJobParams.filterStatus,
      appliedJobParams.isFetchingEnabled,
      appliedJobParams.initialPage,
    );

  const handlePaginationScroll = () => {
    const scrollRef = itemRefs.current?.[appliedJobParams.lastDataLength - 1];
    if (scrollRef) {
      scrollRef?.scrollIntoView();
    }
  };

  const handleScroll = useCallback(
    async (event: React.UIEvent<HTMLDivElement> | Event) => {
      if (isFetchingNextPage || isFetching) return;

      const { scrollTop, scrollHeight, clientHeight } = !isMobile
        ? (event.currentTarget as HTMLDivElement)
        : ((event.target as Document).scrollingElement as HTMLElement);
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (
          appliedJobData?.data?.length >= appliedJobData?.meta?.paging?.total ||
          appliedJobData.isLoadingJobDesc
        )
          return;

        setAppliedJobParams((prev) => ({
          ...prev,
          currentPage: prev.currentPage + 1,
          loadingJobCard: true,
          lastDataLength: appliedJobData.data.length,
        }));
        fetchNextPage();
      }
    },
    [
      isFetchingNextPage,
      isFetching,
      fetchNextPage,
      appliedJobParams,
      appliedJobData,
    ],
  );

  const timeAgo = useCallback(getTimeAppliedAgo, [data]);

  const resetAndRefetch = () => {
    queryClient.removeQueries({
      queryKey: [
        APPLIED_JOBS_QUERY_KEY,
        appliedJobParams.filterStatus,
        appliedJobParams.initialPage,
      ],
      exact: true,
    });
    setAppliedJobParams((prev) => {
      return {
        ...prev,
        initialPage: 1,
        currentPage: 1,
        filterStatus: appliedJobData.selectedFilter?.[0]?.id || "",
        resetType: { value: "filterClick" },
        loadingJobCard: true,
        lastDataLength: appliedJobData.data.length,
      };
    });
  };

  const adjustHeight = useCallback(() => {
    if (containerRef.current) {
      const remainingHeight = getAdjustedHeight("applied");
      containerRef.current.style.height = `${remainingHeight}px`;
    }
  }, [containerRef.current, isMobile]);

  const createViewportResizeHandler = (
    initialHeight: number,
    viewType: "cards" | "desc",
    setAppliedJobData: React.Dispatch<
      React.SetStateAction<AppliedPageDataType>
    >,
  ) => {
    return () => {
      const currentHeight = window.innerHeight;
      let height: number;
      let enabled: boolean;

      if (viewType === "cards") {
        if (currentHeight > initialHeight) {
          height = currentHeight - initialHeight;
          enabled = false;
        } else {
          height = initialHeight - currentHeight;
          enabled = true;
        }
      } else if (viewType === "desc") {
        if (currentHeight < initialHeight) {
          height = currentHeight - initialHeight;
          enabled = true;
        } else {
          height = initialHeight - currentHeight;
          enabled = false;
        }
      }

      setAppliedJobData((prevData: AppliedPageDataType) => {
        let modifiedData = {
          ...prevData,
          extraViewPortData: { height, enabled },
        };
        if (!modifiedData?.createViewportResizeHandler)
          modifiedData.createViewportResizeHandler =
            createViewportResizeHandler;
        return modifiedData;
      });
    };
  };

  const cardClickHandler = (url: string, index: number) => {
    setAppliedJobData((prevData: AppliedPageDataType) => {
      return {
        ...prevData,
        isLoadingJobDesc: true,
        jobDescIndex: { selectedIndex: index },
      };
    });
    handleEventTracking({
      eventName: "Job Card Clicked",
      eventType: "eventPush",
      eventProps: {
        page_name:
          cleverTapPropertyValues[
            appliedJobData.activeTab as keyof typeof cleverTapPropertyValues
          ],
      },
    });
    setCardClickedUrl(url);
  };

  useEffect(() => {
    if (!isMobile) {
      adjustHeight();
      window.addEventListener("resize", adjustHeight);
      return () => {
        window.removeEventListener("resize", adjustHeight);
      };
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    appliedJobParams,
    appliedJobData,
    isMobile,
  ]);

  useEffect(() => {
    if (data) {
      const newFilters = data.pages[data.pages.length - 1]?.filterStatus || [];
      const newMetaData = data.pages[data.pages.length - 1]?.meta || {};
      const newJobs = data.pages[data.pages.length - 1]?.data || [];
      const newFilterStatusPresent =
        newFilters.filter((data: FilterStatusType, index: number) => {
          return data.count > 0;
        }).length > 0 || false;
      setAppliedJobData((prevData: AppliedPageDataType) => {
        const appliedJobsModified = {
          ...prevData,
          data:
            appliedJobParams.currentPage == 1
              ? [...newJobs]
              : [...(prevData?.data || []), ...newJobs],
          filterStatus: [...newFilters],
          meta: { ...newMetaData },
          filterStatusPresent: newFilterStatusPresent,
        };
        if (!prevData?.autoApplyData?.enabled)
          appliedJobsModified["autoApplyData"] = {};
        appliedJobsModified["filterDisable" as keyof AppliedPageDataType] =
          false;
        return appliedJobsModified;
      });
    }
  }, [data]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      if (isFetchingNextPage) {
        container.classList.remove("overflow-y-scroll");
        container.classList.add("overflow-y-hidden");
      } else {
        setTimeout(() => {
          container.classList.remove("overflow-y-hidden");
          container.classList.add("overflow-y-scroll");
        }, 200);
      }
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (appliedJobParams.initialPage === 1) {
      refetch();
    }
  }, [appliedJobParams.resetType]);

  useEffect(() => {
    if (
      appliedJobData.selectedFilter &&
      Array.isArray(appliedJobData.selectedFilter)
    ) {
      if (isMobile) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        const container = containerRef.current;
        if (container) {
          container.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      }

      resetAndRefetch();
    }
  }, [appliedJobData.selectedFilter, isMobile]);

  useEffect(() => {
    if (appliedJobData.isLoadingJobDesc && !appliedJobParams.loadingJobCard) {
      setTimeout(() => {
        setAppliedJobData((prevData: AppliedPageDataType) => {
          const modifiedData = { ...prevData, isLoadingJobDesc: false };
          if (!prevData.leftSideRef) modifiedData.leftSideRef = containerRef;
          if (!prevData.extraViewPortData && isMobile)
            modifiedData.extraViewPortData = { height: 0, enabled: true };
          return modifiedData;
        });
      }, 200);
    } else if (
      !appliedJobData.isLoadingJobDesc &&
      !appliedJobParams.loadingJobCard
    ) {
      handlePaginationScroll();
    }
  }, [appliedJobParams.loadingJobCard]);

  useEffect(() => {
    if (appliedJobParams.loadingJobCard && !appliedJobData.newDataLoader) {
      setTimeout(() => {
        setAppliedJobParams((prev) => ({
          ...prev,
          loadingJobCard: false,
        }));
      }, 200);
    } else if (!appliedJobData.newDataLoader) {
      setTimeout(() => {
        setAppliedJobData((prevData: AppliedPageDataType) => {
          return { ...prevData, isLoadingJobDesc: false };
        });
      }, 200);
    }
  }, [
    appliedJobData.jobDescIndex,
    appliedJobData.data,
    appliedJobData.newDataLoader,
  ]);

  useEffect(() => {
    if (isMobile) {
      const initialHeight = window.innerHeight;
      const handleExtraViewPortJobCards = createViewportResizeHandler(
        initialHeight,
        "cards",
        setAppliedJobData,
      );

      window.addEventListener("resize", handleExtraViewPortJobCards);

      return () => {
        window.removeEventListener("resize", handleExtraViewPortJobCards);
      };
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (maxScroll <= 0) return;

      e.preventDefault();
      let next = container.scrollTop + e.deltaY;
      if (next < 0) next = 0;
      if (next > maxScroll) next = maxScroll;
      container.scrollTop = next;
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [containerRef, isMobile]);

  useEffect(() => {
    if (cardClickedUrl) {
      window.open(cardClickedUrl, isMobile ? "_self" : "_blank");
      setCardClickedUrl(undefined);
    }
  }, [cardClickedUrl]);

  return (
    <div
      ref={containerRef}
      className={`scrollbar-hide applied-jobs-container flex flex-col pb-3`}
      onScroll={handleScroll}
      id="applied-jobs-container"
    >
      {!appliedJobParams.loadingJobCard && !appliedJobData.filterDisable && (
        <div className={`${isMobile ? "!mb-2" : "!mb-3"}`}>
          <p className="!ml-[20px] overflow-hidden truncate text-xs font-normal leading-4 text-[#59566C]">
            Showing{" "}
            <span className="text-xs font-medium text-[#17142A]">
              {appliedJobData?.meta?.paging?.total}{" "}
              {appliedJobData?.meta?.paging?.total > 1 ? "jobs" : "job"}
            </span>
          </p>
        </div>
      )}
      {appliedJobData.data &&
        Array.isArray(appliedJobData.data) &&
        appliedJobData.data.map(
          (data: AppliedJobAPIKeysType, index: number) => {
            let fullName;
            if (index == 3) {
              fullName =
                appliedJobData?.profilePersonalDetails?.personalDetailSection
                  ?.personalDetails?.fullName || "";
            }
            return (
              <div
                key={data?.jobDetails?.companyId || index}
                className={`${appliedJobParams.loadingJobCard || appliedJobData.filterDisable ? "hidden" : ""} ${isMobile ? "mb-3" : "mb-5"}`}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
              >
                {index == 1 ? (
                  <div className="mb-4">
                    <AutoApplyV2 />
                  </div>
                ) : null}
                {index == 3 ? (
                  <a
                    href="/career-services/career-booster"
                    className="cursor-pointer"
                    target="_blank"
                  >
                    <MessageCard
                      key={index}
                      bodyText={`${fullName ? `Hi ${fullName?.split(" ")[0]}, revamp ` : "Revamp "}your resume, plus rank higher to get more call-backs.`}
                      className="bg-message-card mb-[16px]"
                      iconAltText="Rocket Nudge"
                      leftIconSrc={`https://${API_ENDPOINT("").media.rocketNudge}`}
                    />
                  </a>
                ) : null}
                <div>
                  <JobCard
                    key={data?.jobDetails?.companyId || index}
                    cardClickHandler={(url: string) => {
                      cardClickHandler(url, index);
                    }}
                    isMobile={isMobile}
                    jobData={{
                      ...data.jobDetails,
                      jdUrl: getJDUrlWithDomain(
                        data.jobDetails?.jdUrl || "",
                        host,
                      ),
                      successFilled: true,
                      successFilledContent: `${data?.jobDetails?.redirectUrl ? "This job was posted on the external site" : data?.applicationData?.statusName || "Application Sent"} ${data?.applicationData?.statusName !== "No longer in consideration" ? (data?.applicationData?.statusUpdatedAt ? timeAgo(data.applicationData.statusUpdatedAt) : data?.applicationData?.appliedAt ? timeAgo(data.applicationData.appliedAt) : "") : ""}`,
                      appliedBy:
                        (data?.applicationData?.appliedAt &&
                          `Applied ${timeAgo(data?.applicationData?.appliedAt)}`) ||
                        "",
                      applicationSource:
                        data?.applicationData?.applicationSource,
                    }}
                    disableCompanyNameLink={true}
                    variant="applied"
                    pageName="Jobs"
                    siteContext={data?.jobDetails?.siteContext}
                    className="hover:shadow-job-card-hover border-jobCardBorder bg-surface-primary-normal shadow-job-card relative w-auto cursor-pointer border border-solid"
                    fullView={isMobile ? true : false}
                    applyClickHandler={() => {
                      const url = generateJDUrl(data?.jobDetails);
                      setCardClickedUrl(url);
                    }}
                    btnStyleProps="w-1/2 md:!w-fit rounded-full bg-surface-primary-pressed !font-semibold border-0 text-xs text-content-strong py-2 px-3"
                  />
                </div>
              </div>
            );
          },
        )}
      {appliedJobData.data?.length <= 1 && (
        <div className="mb-4">
          <AutoApplyV2 />
        </div>
      )}
      {Array(appliedJobData.selectedFilter?.[0]?.count || 10)
        .fill(0)
        .map(() => {
          return (
            <div
              className={`${appliedJobParams.loadingJobCard || appliedJobData.filterDisable ? "" : "hidden"} ${isMobile ? "mb-3" : "mb-5"}`}
            >
              <Skeleton />
            </div>
          );
        })}
    </div>
  );
};

export default React.memo(AppliedJobCard);
